import React, { ButtonHTMLAttributes, useLayoutEffect, useState } from 'react';
import styled from 'styled-components';
import { usePageScrollPosition, useWindowDimensions } from 'packages/smart-home-support/hooks';
import {
  BREAKPOINT_DESKTOP,
  BREAKPOINT_PX_DESKTOP,
  HEIGHT_MOBILE as HEADER_HEIGHT_MOBILE,
  HEIGHT_DESKTOP as HEADER_HEIGHT_DESKTOP,
} from 'packages/smart-home-support/components/Header/constants';
import { TabNavProps } from './models';
import { getAccentColor, getBreakpoint } from '../../utils';
import { withAnalyticOnEvent } from 'react-shisell';
import BREAK_POINTS from 'packages/smart-home-support/styling/media-queries';

const TAB_NAV_HEIGHT = 50;
const breakpoint = getBreakpoint();

const s = {
  TabNavContainer: styled.div`
    position: relative;
  `,
  TabsPlaceholder: styled.div<{ isSticky: boolean }>`
    height: ${TAB_NAV_HEIGHT}px;
  `,
  Tabs: styled.div<{ isSticky: boolean; indicatorRefElement?: HTMLSpanElement }>`
    display: flex;
    justify-content: space-between;
    height: ${TAB_NAV_HEIGHT}px;
    position: absolute;
    left: 0;
    right: 0;
    background: #fff;
    transform: translateY(-100%);

    ${(props) =>
      props.isSticky &&
      `
      position: fixed;
      transform: none;
      top: ${HEADER_HEIGHT_MOBILE}px;
      z-index: 5;
      margin: 0 25px;
      @media ${BREAKPOINT_DESKTOP} {
        top: ${HEADER_HEIGHT_DESKTOP}px;
      }
    `}

    @media ${breakpoint} {
      position: absolute;
      top: auto;
      transform: translateY(-100%);
      margin: 0;
    }
    
    @media ${BREAK_POINTS.desktopBig} {
      justify-content: left;
    }

    /* Active tab indicator */
    :before {
      content: '';
      position: absolute;
      background: ${getAccentColor()};
      z-index: 1;
      bottom: 0;
      height: 6px;
      ${(props) =>
        props.indicatorRefElement &&
        `
        transition: left 0.5s, width 0.5s;
        width: ${props.indicatorRefElement.clientWidth}px;
        left: ${props.indicatorRefElement.offsetLeft}px;
      `}
    }

    /* Background and bottom border */
    :after {
      content: '';
      display: block;
      background: #fff;
      position: absolute;
      top: 0;
      bottom: 0;
      margin: auto;
      z-index: -1;
      border-bottom: 1px solid #747676;
      @media ${breakpoint} {
        left: 0;
        right: 0;
      }
    }
  `,
  Tab: withAnalyticOnEvent<ButtonHTMLAttributes<HTMLButtonElement> & { tabCount: number }>({
    analyticName: 'SH_WEB_EVENT',
    eventName: 'onClick',
    extras: { Tags: ['atlas'], ActionId: 'Click' },
  })(styled.button<{ tabCount: number }>`
    display: flex;
    justify-content: center;
    flex-direction: row;
    align-items: center;
    height: 100%;
    background: none;
    font-weight: bold;
    border: none;
    outline: none;
    margin: 0;
    padding: 0 12.5px;
    font-size: 13px;
    &:first-child {
      padding-left: 0;
    }
    &:last-child {
      padding-right: 0;
    }
    @media ${breakpoint} {
      font-size: 20px;
      padding: 0;
      align-items: flex-start;
    }
    
    @media ${BREAK_POINTS.desktopBig} {
      padding-right: 100px;
      max-width: 305px;
    }
  `),
  TabInner: styled.span<{ isActive: boolean }>`
    display: flex;
    flex-grow: 1;
    align-items: center;
    position: relative;
  `,
  TabPanels: styled.div`
    position: relative;
    display: flex;
  `,
  TabPanel: styled.div<{ isSticky: boolean; isActive: boolean }>`
    margin-top: 35px;
    width: 100%;
    transition: opacity ${(props) => {
      if (!props.isSticky) {
        return '0.5s';
      }
      if (props.isActive) {
        return '0.5s 1ms';
      }
      return '0s';
    }};
    display: ${(props) => (props.isActive ? 'block' : 'none')};
    position: ${(props) => (props.isActive ? 'static' : 'absolute')}
    z-index: ${(props) => (props.isActive ? 0 : -1)};
    opacity: ${(props) => (props.isActive ? 1 : 0)};
    @media ${breakpoint} {
      margin-top: 50px;
    }
  `,
};

type TabInnerRefElement = HTMLSpanElement;
type TabInnerRef = React.RefObject<TabInnerRefElement>;

const TabNav: React.FC<TabNavProps<string, any>> = (props) => {
  const { isSticky = false, tabs, setActiveTabKey } = props;
  const activeTabKey = props.activeTabKey;
  const [tabsPosition, setTabsPosition] = useState<number>();
  const [isCurrentlySticky, setIsCurrentlySticky] = useState(false);
  const [tabInnerRefs, setTabInnerRefs] = useState<Array<TabInnerRef>>([]);
  const [activeTabRef, setActiveTabRef] = useState<TabInnerRef>();
  const [topOffset, setTopOffset] = useState<number>(0);
  const pageScrollPosition = usePageScrollPosition();
  const windowDimensions = useWindowDimensions();
  const tabsRef = React.createRef<HTMLDivElement>();

  useLayoutEffect(() => {
    setTabInnerRefs(tabs.map(() => React.createRef()));
  }, []);

  useLayoutEffect(() => {
    setTopOffset(windowDimensions[0] < BREAKPOINT_PX_DESKTOP ? HEADER_HEIGHT_MOBILE : HEADER_HEIGHT_DESKTOP);
  }, [windowDimensions]);

  useLayoutEffect(() => {
    if (tabsRef && tabsRef.current) {
      setTabsPosition(tabsRef.current.getBoundingClientRect().top + pageScrollPosition - topOffset);
    }
  }, [pageScrollPosition, topOffset]);

  useLayoutEffect(() => {
    if (tabsPosition !== undefined) {
      setIsCurrentlySticky(isSticky && pageScrollPosition > tabsPosition);
    }
  }, [tabsPosition, pageScrollPosition]);

  useLayoutEffect(() => {
    const activeTabIndex = tabs.findIndex((tab) => tab.key === activeTabKey);
    setActiveTabRef(tabInnerRefs[activeTabIndex]);
  }, [activeTabKey, tabInnerRefs]);

  const onTabClick = (tabKey: string) => {
    setActiveTabKey(tabKey);
    if (tabsPosition && pageScrollPosition > tabsPosition) {
      window.scrollTo(0, tabsPosition);
    }
  };

  return (
    <s.TabNavContainer>
      <s.TabsPlaceholder ref={tabsRef} isSticky={isCurrentlySticky} />
      <s.Tabs
        isSticky={isCurrentlySticky}
        indicatorRefElement={activeTabRef && activeTabRef.current ? activeTabRef.current : undefined}
      >
        {tabs.map((tab, i) => (
          <s.Tab
            className={`dashboard-tab-${tab.key}`}
            key={tab.key}
            tabCount={tabs.length}
            onClick={() => onTabClick(tab.key)}
            analyticsExtras={{ ElementId: `dashboard-tab-${tab.key}` }}
          >
            <s.TabInner ref={tabInnerRefs[i]} isActive={tab.key === activeTabKey}>
              {tab.label}
            </s.TabInner>
          </s.Tab>
        ))}
      </s.Tabs>
      <s.TabPanels>
        {tabs.map((tab, i) => (
          <s.TabPanel key={i} isSticky={isCurrentlySticky} isActive={tab.key === activeTabKey}>
            <tab.Component {...props.tabProps} activeTabKey={activeTabKey} setActiveTabKey={setActiveTabKey} />
          </s.TabPanel>
        ))}
      </s.TabPanels>
    </s.TabNavContainer>
  );
};

export default TabNav;



// WEBPACK FOOTER //
// ./packages/smart-home-support/pages/profile/components/TabNav/TabNav.tsx